import {
  DropDown,
  FlexColumn,
  FormControls,
  TextField,
  ToggleButton,
} from '@elementary/common-ui/components';
import { ToggleButtonGroup, Typography } from '@mui/material';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  UseFormRegister,
} from 'react-hook-form';
import {
  anomalyDirectionOptions,
  detectionPeriodOptions,
} from 'shared/constants';
import { VolumeDetectionMode } from 'shared/types';
import { themeTokens } from '@elementary/common-ui/style/tokens';

export const DetectionModeField = ({
  mode,
  onChange,
  readOnly,
}: {
  mode: VolumeDetectionMode;
  onChange: (next: VolumeDetectionMode) => void;
  readOnly?: boolean;
}) => (
  <FormControls.FieldsWrapper>
    <FlexColumn sx={{ gap: 2 }}>
      <FormControls.FormTitle>Detection mode</FormControls.FormTitle>
      <ToggleButtonGroup
        fullWidth
        value={mode}
        exclusive
        disabled={readOnly}
        onChange={(_, val) => {
          if (val) onChange(val as VolumeDetectionMode);
        }}
      >
        <ToggleButton value="anomaly_detection" size="large">
          Anomaly detection
        </ToggleButton>
        <ToggleButton value="percentage_thresholds" size="large">
          Fixed percentage threshold
        </ToggleButton>
      </ToggleButtonGroup>
    </FlexColumn>
  </FormControls.FieldsWrapper>
);

export const AnomalyDirectionField = <T extends FieldValues>({
  control,
  name,
  readOnly,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  readOnly?: boolean;
}) => (
  <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
    <FormControls.FormTitle>Anomaly direction</FormControls.FormTitle>
    <FormControls.Row style={{ gap: themeTokens.space.sm, flexWrap: 'wrap' }}>
      <FormControls.FormText>Detect</FormControls.FormText>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DropDown
            sortOptions={false}
            options={anomalyDirectionOptions}
            sx={{ minWidth: '180px' }}
            {...field}
            disabled={readOnly || field.disabled}
          />
        )}
      />
      <FormControls.FormText>
        {`in the table's row count.`}
      </FormControls.FormText>
    </FormControls.Row>
  </FormControls.FieldsWrapper>
);

export const DetectionPeriodField = <T extends FieldValues>({
  control,
  register,
  countName,
  periodName,
  readOnly,
}: {
  control: Control<T>;
  register: UseFormRegister<T>;
  countName: FieldPath<T>;
  periodName: FieldPath<T>;
  readOnly?: boolean;
}) => (
  <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
    <FormControls.FormTitle>Detection period</FormControls.FormTitle>
    <FormControls.Row style={{ gap: themeTokens.space.sm, flexWrap: 'wrap' }}>
      <Typography component="span">Anomalies during the last</Typography>
      <TextField
        sx={{ width: '50px', minWidth: '50px' }}
        {...register(countName)}
        disabled={readOnly}
      />
      <Controller
        control={control}
        name={periodName}
        render={({ field }) => (
          <DropDown
            sortOptions={false}
            options={detectionPeriodOptions}
            sx={{ minWidth: '100px' }}
            {...field}
            disabled={readOnly || field.disabled}
          />
        )}
      />
      <Typography component="span" sx={{ wordBreak: 'break-word' }}>
        will impact the test result.
      </Typography>
      <Typography component="span" sx={{ wordBreak: 'break-word' }}>
        Rest of the time will be used as the training period.
      </Typography>
    </FormControls.Row>
  </FormControls.FieldsWrapper>
);
